import { useRef, type ReactNode } from "react";
import type { Section, SectionStatus } from "@k8s-dashboard/contracts";
import { useAsyncErrorFocus } from "@/app/focus";
import { useT } from "@/i18n/I18nProvider";

/**
 * 공통 상태 (이슈 #13 / #14 완료 기준)
 * --------------------------------------------------------------------------
 * "데이터 없음 · 권한 없음 · upstream 장애"는 서로 **다르게** 보여야 합니다.
 * 세 경우를 같은 회색 빈 화면으로 처리하면 운영자가 원인을 오판합니다.
 *
 * - loading   : 골격. 실제 높이를 미리 차지해 값이 도착해도 레이아웃이 흔들리지 않습니다.
 * - empty     : 조회는 성공했고 결과가 0건. 보통 좋은 소식입니다.
 * - forbidden : 권한 부족. 필요한 역할을 문구로 알려줍니다.
 * - degraded  : 데이터소스 장애. 마지막 값이 있으면 stale 표시와 함께 계속 보여줍니다.
 * - error     : 화면 단위 실패. 재시도 버튼을 달고, 처음 나타난 순간 포커스를 받습니다.
 *
 * 문구는 카탈로그에서 옵니다(ADR 0025). 서버가 준 **사유 코드**는 그대로 두고,
 * 문장은 여기서 고릅니다 — 서버가 문장을 만들면 언어가 서버에 묶입니다.
 */

export function LoadingState({ lines = 3, height = 160 }: { lines?: number; height?: number }) {
  const { t } = useT("common");
  return (
    <div className="state" style={{ minHeight: height, alignItems: "stretch", justifyContent: "flex-start" }}>
      <span className="skeleton" style={{ height: 14, width: "38%" }} />
      {Array.from({ length: lines }).map((_, i) => (
        <span key={i} className="skeleton" style={{ height: 12, width: `${92 - i * 11}%` }} />
      ))}
      <span className="visually-hidden" role="status">
        {t("common.state.loading")}
      </span>
    </div>
  );
}

export function EmptyState({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="state">
      <span className="state__glyph" aria-hidden="true">
        ✓
      </span>
      <span className="state__title">{title}</span>
      {detail && <span className="state__detail">{detail}</span>}
    </div>
  );
}

export function ForbiddenState({ detail }: { detail?: string }) {
  const { t } = useT("common");
  return (
    <div className="state state--forbidden">
      <span className="state__glyph" aria-hidden="true">
        !
      </span>
      <span className="state__title">{t("common.state.forbidden.title")}</span>
      <span className="state__detail">
        {detail ?? t("common.state.forbidden.detail")} {t("common.state.forbidden.note")}
      </span>
    </div>
  );
}

/**
 * 화면 단위 실패.
 *
 * 오류가 **처음 나타난 순간에만** 이 알림으로 포커스를 옮깁니다(ADR 0025). 같은
 * 오류가 재렌더될 때는 옮기지 않으므로 사용자가 입력하던 자리에서 계속 튕겨
 * 나가지 않고, 오류가 사라졌다 다시 나면 새 episode로 한 번 더 옮깁니다.
 * `episode`를 넘기지 않으면 detail이 곧 episode입니다.
 */
export function ErrorState({ detail, onRetry, episode }: { detail?: string; onRetry?: () => void; episode?: string }) {
  const { t } = useT("common");
  const alert = useRef<HTMLDivElement>(null);
  useAsyncErrorFocus(episode ?? detail ?? "error", alert);
  return (
    <div className="state state--error" role="alert" tabIndex={-1} ref={alert}>
      <span className="state__glyph" aria-hidden="true">
        ✕
      </span>
      <span className="state__title">{t("common.state.error.title")}</span>
      {detail && <span className="state__detail">{detail}</span>}
      {onRetry && (
        <button type="button" className="linkish" onClick={onRetry}>
          {t("common.state.error.retry")}
        </button>
      )}
    </div>
  );
}

export function DegradedState({ source, detail }: { source?: string; detail?: string }) {
  const { t } = useT("common");
  /* 서버가 주는 것은 사유 **코드**입니다. 문장은 이 표에서 고르고, 모르는 코드는
     원문 그대로 둡니다 — 지어낸 문장이 서버가 아는 사실을 덮지 않습니다. */
  const historyNotConfigured = detail === "history_not_configured";
  const historyScopeTooLarge = detail === "history_scope_too_large";
  const topologyNotConfigured = detail === "topology_not_configured";
  const alertsNotConfigured = detail === "alerts_not_configured";
  const safeDetail = historyNotConfigured
    ? t("common.state.degraded.history.detail")
    : historyScopeTooLarge
      ? t("common.state.degraded.historyScope.detail")
      : topologyNotConfigured
      ? t("common.state.degraded.topology.detail")
      : alertsNotConfigured
        ? t("common.state.degraded.alerts.detail")
        : detail;
  const title = historyNotConfigured
    ? t("common.state.degraded.history.title")
    : historyScopeTooLarge
      ? t("common.state.degraded.historyScope.title")
      : topologyNotConfigured
      ? t("common.state.degraded.topology.title")
      : alertsNotConfigured
        ? t("common.state.degraded.alerts.title")
        : source
      ? t("common.state.degraded.titleWithSource", { source })
      : t("common.state.degraded.title");
  return (
    <div className="state state--degraded">
      <span className="state__glyph" aria-hidden="true">
        ▲
      </span>
      <span className="state__title">{title}</span>
      <span className="state__detail">{safeDetail ?? t("common.state.degraded.detail")}</span>
    </div>
  );
}

/* 데이터소스 이름은 제품 이름입니다 — 번역하지 않습니다. */
const SOURCE_LABEL: Record<string, string> = {
  greptimedb: "GreptimeDB",
  quickwit: "Quickwit",
  kubernetes: "Kubernetes API",
  alertmanager: "Alertmanager",
  /* 해소 이력 원천(ADR 0049). Alertmanager 장애와 다른 사실이므로 이름을 나눕니다. */
  grafana: "Grafana",
};

/**
 * Section<T> 봉투를 상태별로 렌더링합니다.
 * degraded인데 마지막 값이 있으면 값을 그리고, 없을 때만 상태 화면을 그립니다.
 */
export function SectionView<T>({
  section,
  loading,
  emptyTitle,
  emptyDetail,
  children,
}: {
  section: Section<T> | undefined;
  loading: boolean;
  emptyTitle: string;
  emptyDetail?: string;
  children: (data: T, status: SectionStatus) => ReactNode;
}) {
  if (loading || !section) return <LoadingState />;
  if (section.status === "forbidden") return <ForbiddenState detail={section.reason} />;
  if (section.status === "empty") return <EmptyState title={emptyTitle} detail={emptyDetail} />;
  if (section.status === "degraded" && section.data === undefined) {
    return <DegradedState source={section.source ? SOURCE_LABEL[section.source] : undefined} detail={section.reason} />;
  }
  if (section.data === undefined) return <EmptyState title={emptyTitle} detail={emptyDetail} />;
  return <>{children(section.data, section.status)}</>;
}

export function sourceLabel(source?: string) {
  return source ? (SOURCE_LABEL[source] ?? source) : undefined;
}
